import {
  TimeSlot,
  LocationType,
  ListAvailabilityTimeSlotsResponse,
} from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { LayoutOptions } from '../../types/types';
import { STAFF_RESOURCE_ID } from '../../constants/constants';

const toTime = (num: number) => {
  const s = '00' + num;
  return s.substr(s.length - 2) + ':00';
};

interface SlotOptions {
  startHour: number;
  duration: number;
  openSpots: number;
  staffMember: string;
  locationName: string;
}

function createLocalDateTime(date: Date, hour: number) {
  return `${date.getFullYear()}-${
    date.getMonth() + 1
  }-${date.getDate()}T${hour}:00:00`;
}

function createTimeSlot(date: Date, options: SlotOptions): TimeSlot {
  const { startHour, duration, openSpots, staffMember, locationName } = options;
  return {
    localStartDate: createLocalDateTime(date, startHour),
    localEndDate: createLocalDateTime(date, startHour + duration),
    bookable: true,
    location: {
      locationType: LocationType.BUSINESS,
      formattedAddress: 'Shlomo Ibn Gabirol Street 114',
      id: 'location-1-id',
      name: locationName,
    },
    availableResources: [
      {
        resourceTypeId: STAFF_RESOURCE_ID,
        resources: [
          {
            id: 'staff-1-id',
            name: staffMember,
          },
        ],
      },
    ],
    bookableCapacity: openSpots,
  };
}

function getWeeklyTimeslotsDailySlotsCount(dayIndex: number) {
  switch (dayIndex) {
    case 2:
    case 3:
      return 6;
    case 4:
      return 0;
    default:
      return 4;
  }
}

function getWeeklyTimetableDailySlotsCount(dayIndex: number) {
  switch (dayIndex) {
    case 0:
      return 2;
    case 4:
      return 1;
    case 5:
      return 0;
    default:
      return 3;
  }
}

const getDailySlots = (
  date: Date,
  slotsCount: number,
  optionsFactory: (index: number) => SlotOptions,
) =>
  new Array(slotsCount)
    .fill('')
    .map((_, index): TimeSlot => createTimeSlot(date, optionsFactory(index)));

const getDefaultSlotOptions = (index: number): SlotOptions => ({
  startHour: index,
  duration: 1,
  openSpots: 5,
  staffMember: 'Staff 1',
  locationName: 'Tel Aviv',
});

const getWeeklyTimetableSlotOptions =
  ({ translations: { t } }: ControllerFlowAPI) =>
  (index: number): SlotOptions => ({
    startHour: 8 + index * 2,
    duration: 2,
    openSpots: 3,
    staffMember: t('dummy-content.service.staff'),
    locationName: t('dummy-content.service.location'),
  });

export function createDummySlots({
  flowAPI,
  settingsParams,
  from,
}: {
  flowAPI: ControllerFlowAPI;
  settingsParams: any;
  from?: string;
}): ListAvailabilityTimeSlotsResponse {
  let timeSlots: TimeSlot[] = [];
  const date = from ? new Date(from) : new Date();
  const calendarLayout = flowAPI.settings.get(settingsParams.calendarLayout);

  switch (calendarLayout) {
    case LayoutOptions.WEEKLY_TIME_SLOTS:
      for (let dayInTheWeek = 0; dayInTheWeek < 7; dayInTheWeek++) {
        date.setDate(date.getDate() + 1);
        const numberOfSlots = getWeeklyTimeslotsDailySlotsCount(dayInTheWeek);
        const availabilityEntriesForThisDay = getDailySlots(
          date,
          numberOfSlots,
          getDefaultSlotOptions,
        );
        timeSlots.push(...availabilityEntriesForThisDay);
      }
      break;

    case LayoutOptions.WEEKLY_TIMETABLE:
      for (let dayInTheWeek = 0; dayInTheWeek < 7; dayInTheWeek++) {
        date.setDate(date.getDate() + 1);
        const numberOfSlots = getWeeklyTimetableDailySlotsCount(dayInTheWeek);
        const availabilityEntriesForThisDay = getDailySlots(
          date,
          numberOfSlots,
          getWeeklyTimetableSlotOptions(flowAPI),
        );
        timeSlots.push(...availabilityEntriesForThisDay);
      }
      break;

    case LayoutOptions.DAILY_AGENDA_WEEKLY_PICKER:
      const availabilityEntriesForToday = getDailySlots(
        new Date(),
        3,
        getWeeklyTimetableSlotOptions(flowAPI),
      );
      timeSlots.push(...availabilityEntriesForToday);
      break;

    default:
      const nextYearDate = new Date();
      nextYearDate.setFullYear(nextYearDate.getFullYear() + 1);
      timeSlots = new Array(18).fill('').map((_, index): TimeSlot => {
        nextYearDate.setDate(index + 1);
        return createTimeSlot(nextYearDate, getDefaultSlotOptions(index));
      });
      break;
  }

  return {
    timeSlots,
  };
}
